"use client"

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";


const PLATE_REGEX = /^[A-Z]{2}\d{5}$|^[A-Z]{2}\d{2}\d{3}$/;
const STORAGE_KEY = "wheeltox_last_plate";

export default function HeroSection() {
  const [licensePlate, setLicensePlate] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const savedPlate = localStorage.getItem(STORAGE_KEY);
    if (savedPlate) {
      setLicensePlate(savedPlate);
    }
  }, []);

  const validatePlate = (plate: string): boolean => {
    if (!plate.trim()) {
      setError("Please enter a license plate number");
      return false;
    }
    
    const cleanPlate = plate.trim().toUpperCase();
    if (!PLATE_REGEX.test(cleanPlate)) {
      setError("Please enter a valid Danish plate (e.g., AB12345)");
      return false;
    }
    
    setError("");
    return true;
  };

  const handleShopWheels = () => {
    if (!licensePlate.trim()) {
      router.push("/products");
      return;
    }

    if (!validatePlate(licensePlate)) {
      return;
    }

    const cleanPlate = licensePlate.trim().toUpperCase();

    localStorage.setItem(STORAGE_KEY, cleanPlate);

    setIsLoading(true);

    router.push(`/products?plate=${encodeURIComponent(cleanPlate)}`);
  };

  const handleSellWheels = () => {
    router.push("/sell-your-wheel");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setLicensePlate(value);
    if (error) setError("");
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Image
        src="/image/home/hero-truck.png"
        alt="Hero background"
        fill
        className="object-cover"
        priority
      />
      <div 
        className="absolute inset-0" 
        style={{ background: "linear-gradient(287.56deg, rgba(0, 0, 0, 0) 31.24%, rgba(0, 0, 0, 0.6) 49.83%)" }}
      />

      <div className="absolute inset-0 flex items-end">
        <div className="px-4 md:px-8 lg:px-20 pb-12 sm:pb-16 md:pb-20">
          <div className="max-w-[649px]">
          <h1 className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 md:mb-4">
            THE SMARTER WAY TO BUY AND SELL WHEELS.
          </h1>

          <p className="font-plus-jakarta text-sm sm:text-base md:text-lg text-white mb-4 sm:mb-6 md:mb-8">
            Buy quality refurbished wheels or sell yours to give them new life
          </p>

          <div className="space-y-3 sm:space-y-4">
            <p className="font-plus-jakarta text-white/80 text-xs sm:text-sm">
              Enter your license plate number to find compatible wheels
            </p>

            <div className="flex flex-col">
              <div className={`flex bg-background-100 rounded-lg sm:rounded-xl overflow-hidden w-full max-w-[643px] ${error ? 'ring-2 ring-red-500' : ''}`}>
                <div className="px-2 sm:px-4 py-2 sm:py-3 border-r border-background-50 flex items-center bg-background-50">
                  <span className="font-plus-jakarta text-base sm:text-xl md:text-2xl font-semibold text-neutral-900">DK</span>
                </div>
                <input
                  type="text"
                  placeholder="AB12345"
                  value={licensePlate}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleShopWheels();
                    }
                  }}
                  maxLength={7}
                  className="font-plus-jakarta font-semibold flex-1 border-0 outline-none px-2 sm:px-4 py-1 text-neutral-900 bg-transparent text-base sm:text-xl md:text-[30px] placeholder:tracking-[0.1em] placeholder:text-base sm:placeholder:text-xl md:placeholder:text-[30px] placeholder:text-neutral-100"
                />
              </div>
              {error && (
                <p className="font-plus-jakarta text-red-400 text-sm mt-2">{error}</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
              <Button
                onClick={handleSellWheels}
                disabled={isLoading}
                className="font-plus-jakarta font-bold bg-primary hover:bg-primary/90 text-white w-full sm:w-[316.5px] h-12 sm:h-14 md:h-[70px] rounded-xl px-4 sm:px-6 py-[10px] text-sm sm:text-base md:text-lg cursor-pointer disabled:opacity-70"
              >
                Sell Wheels
              </Button>
              <Button
                onClick={handleShopWheels}
                disabled={isLoading}
                className="font-plus-jakarta font-bold bg-primary hover:bg-primary/90 text-white w-full sm:w-[316.5px] h-12 sm:h-14 md:h-[70px] rounded-xl px-4 sm:px-6 py-[10px] text-sm sm:text-base md:text-lg cursor-pointer disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  "Shop Wheels"
                )}
              </Button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
